import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Platform } from '@/types';
import { Plus, X, Mail, Eye, EyeOff } from "lucide-react";
import { PlatformSelect } from "@/components/forms/PlatformSelect";
import { getFirebaseServices } from "@/lib/firebase/client";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp, writeBatch, updateDoc, arrayUnion, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getReferralCode, clearReferralCode } from "@/lib/referral-cookie";

interface SignupAccount {
  id: string;
  platform: Platform;
  handle: string;
}

const createRow = (): SignupAccount => ({
  id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 9),
  platform: "instagram",
  handle: "",
});

export function SignupForm() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<SignupAccount[]>([createRow()]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const addRow = () => {
    if (accounts.length >= 15) {
      setStatus("You can link up to 15 social accounts at launch.");
      return;
    }
    setAccounts((prev) => [...prev, createRow()]);
  };

  const updateAccount = (id: string, payload: Partial<SignupAccount>) => {
    setAccounts((prev) => prev.map((row) => (row.id === id ? { ...row, ...payload } : row)));
  };

  const removeAccount = (id: string) => {
    if (accounts.length === 1) {
      setStatus("You must have at least one social account.");
      return;
    }
    setAccounts((prev) => prev.filter((row) => row.id !== id));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setStatus(null);

    // Require at least one social account handle to be filled
    const hasAccount = accounts.some((a) => a.handle && a.handle.trim().length > 0);
    if (!hasAccount) {
      setStatus("Please add at least one social account (enter a channel handle).");
      setLoading(false);
      return;
    }

    // Require acceptance of terms and conditions
    if (!acceptedTerms) {
      setStatus("You must accept the Terms and Conditions to sign up.");
      setLoading(false);
      return;
    }

    const services = getFirebaseServices();
    if (!services) {
      setStatus("Firebase not configured. Add .env.local with Firebase keys.");
      setLoading(false);
      return;
    }

    const { auth, db } = services;

    try {
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCred.user);

      // Generate unique affiliate code
      const functions = getFunctions();
      const generateCode = httpsCallable(functions, 'generateUniqueAffiliateCode');
      const codeResult = await generateCode();
      const affiliateCode = (codeResult.data as any)?.code || '';

      // Check for referral code from cookies
      const referralCode = getReferralCode();
      let referredBy = null;
      let referredByCode = null;

      if (referralCode) {
        try {
          const validateCode = httpsCallable(functions, 'validateAffiliateCode');
          const validationResult = await validateCode({ code: referralCode });
          const validation = validationResult.data as any;
          
          if (validation.valid && validation.referrerUid !== userCred.user.uid) {
            // Check referrer's creation date to prevent mutual referrals
            const referrerRef = doc(db, 'users', validation.referrerUid);
            const referrerSnap = await getDoc(referrerRef);
            
            if (referrerSnap.exists()) {
              const referrerCreatedAt = referrerSnap.data().createdAt?.toDate();
              const now = new Date();
              
              // Only link if referrer account is older (even by a second)
              if (referrerCreatedAt && referrerCreatedAt < now) {
                referredBy = validation.referrerUid;
                referredByCode = referralCode;
                clearReferralCode(); // Clear cookie after successful link
              } else {
                console.warn('Referrer account is not older than current time');
              }
            }
          }
        } catch (err) {
          console.error('Failed to validate referral code:', err);
        }
      }

      // Create user doc with affiliate fields
      await setDoc(doc(db, "users", userCred.user.uid), {
        email,
        displayName: null,
        photoUrl: null,
        verified: false,
        defaultAccountId: null,
        paypalEmail: null,
        totalEarned: 0,
        pendingPayout: 0,
        lastPayoutDate: new Date('2025-12-01T00:00:00Z'),
        affiliateCode,
        affiliateEarnings: 0,
        referrals: [],
        referredBy,
        referredByCode,
        referralLinkedAt: referredBy ? serverTimestamp() : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // If referred, add to referrer's referrals list
      if (referredBy) {
        try {
          await updateDoc(doc(db, "users", referredBy), {
            referrals: arrayUnion(userCred.user.uid),
          });
        } catch (err) {
          console.error('Failed to update referrer:', err);
        }
      }

      // Create social account docs
      const batch = writeBatch(db);
      for (const acc of accounts) {
        if (!acc.handle.trim()) continue;
        const accRef = doc(db, "socialAccounts", crypto.randomUUID());
        batch.set(accRef, {
          userId: userCred.user.uid,
          platform: acc.platform,
          handle: acc.handle.trim(),
          externalId: null,
          metadata: {},
          slotsUsed: 0,
          slotsLimit: 3,
          status: "pending",
          createdAt: serverTimestamp(),
        });
      }
      await batch.commit();

      setShowVerificationPrompt(true);
      setStatus(
        `✅ Signup successful! Verification email sent to ${email}. Please check your inbox (and spam folder) for the verification link.`
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Signup failed. Try again.";
      setStatus(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const checkVerification = async () => {
    setVerifying(true);
    setStatus(null);
    try {
      const services = getFirebaseServices();
      if (!services) {
        setStatus("Firebase not configured.");
        setVerifying(false);
        return;
      }
      const { auth } = services;
      const user = auth.currentUser;
      if (!user) {
        setStatus("No user found. Please try signing up again.");
        setVerifying(false);
        return;
      }
      await user.reload();
      if (user.emailVerified) {
        setStatus("✅ Email verified! Redirecting...");
        setTimeout(() => navigate('/campaigns'), 1500);
      } else {
        setStatus("❌ Not yet verified. Please check your inbox (and spam folder) and click the verification link.");
      }
    } catch (err: any) {
      console.error('Verification check error', err);
      setStatus(err.message || 'Failed to check verification status');
    } finally {
      setVerifying(false);
    }
  };

  if (showVerificationPrompt) {
    return (
      <div className="space-y-4 rounded-3xl border border-white/10 bg-zinc-900/70 p-6 text-white text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-lime-400/20 flex items-center justify-center">
          <Mail className="h-8 w-8 text-lime-400" />
        </div>
        <h2 className="text-2xl font-semibold">Check your email</h2>
        <p className="text-sm text-white/60">
          We sent a verification link to <span className="font-medium text-white">{email}</span>. Click the link in the email to verify your account — check your inbox and spam folder if you don't see it.
        </p>
        <button
          onClick={checkVerification}
          disabled={verifying}
          className="w-full rounded-2xl bg-lime-400 px-4 py-3 text-center font-semibold text-black disabled:opacity-50"
        >
          {verifying ? "Checking..." : "Verification completed"}
        </button>
        {status && (
          <p
            className={
              status.startsWith("✅")
                ? "text-sm text-emerald-300"
                : status.startsWith("❌")
                ? "text-sm text-red-300"
                : "text-sm text-white/70"
            }
          >
            {status}
          </p>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto w-full max-w-md sm:max-w-lg lg:max-w-xl space-y-4 rounded-3xl border border-white/10 bg-zinc-900/70 p-5 sm:p-6 md:p-8 text-white"
    >
      <div>
        <h1 className="text-2xl font-semibold">Creator signup</h1>
        <p className="text-sm text-white/60">
          Add your faceless channels, then verify your email to access the console.
        </p>
      </div>

      <label className="text-xs uppercase text-white/60">Email</label>
      <input
        type="email"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        className="w-full rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
      />
      <label className="text-xs uppercase text-white/60">Password</label>
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="w-full rounded-2xl border border-white/10 bg-white/5 px-3 py-2 pr-10 text-sm"
        />
        <button
          type="button"
          onClick={() => setShowPassword((s) => !s)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-md text-white/70"
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-m font-semibold">Social accounts</p>
        <button type="button" onClick={addRow} className="flex items-center gap-1 text-xs text-lime-300">
          <Plus className="h-4 w-4" /> Add account
        </button>
      </div>
      <p className="text-xs text-red-300">Enter the username of your channel (for example <span className="font-mono">"MrBeast6000"</span>)</p>

      <div className="space-y-3">
        {accounts.map((row, index) => (
          <div key={row.id} className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/40 p-3">
            <PlatformSelect
              value={row.platform}
              onChange={(value) => updateAccount(row.id, { platform: value })}
            />
            <input
              value={row.handle}
              onChange={(event) => updateAccount(row.id, { handle: event.target.value })}
              placeholder={`@channel${index + 1}`}
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
            />
            {/* platform icon removed per request */}
            <button
              type="button"
              onClick={() => removeAccount(row.id)}
              className="flex h-10 aspect-square items-center justify-center rounded-2xl border border-white/10 bg-red-500/20 text-red-400 hover:bg-red-500/30 transition"
              title="Remove account"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Terms and Conditions Checkbox */}
      <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/40 p-4">
        <input
          type="checkbox"
          id="accept-terms"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-lime-400 focus:ring-lime-400 focus:ring-offset-0"
        />
        <label htmlFor="accept-terms" className="text-sm text-white/80">
          I have read and agree to the{" "}
          <Link
            to="/terms"
            target="_blank"
            className="font-medium text-lime-400 hover:text-lime-300 underline"
          >
            Terms and Conditions
          </Link>
        </label>
      </div>

      <button
        type="submit"
        disabled={loading || !acceptedTerms}
        title={!acceptedTerms ? "Please accept the Terms and Conditions to continue" : undefined}
        className="w-full rounded-2xl bg-lime-400 px-4 py-3 text-center font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? "Creating account..." : "Complete signup"}
      </button>
      {status && (
        <p
          className={
            status.startsWith("✅")
              ? "text-sm text-emerald-300"
              : "text-sm text-red-300"
          }
        >
          {status}
        </p>
      )}

      <div className="pt-4 text-center text-sm text-white/60">
        Already have an account?{" "}
        <Link to="/auth/login" className="font-medium text-lime-400 hover:text-lime-300">
          Sign in
        </Link>
      </div>
    </form>
  );
}
